/**
 * BillingPage - vue d ensemble du plan + comparatif des offres.
 *
 * v3 (post archi B) :
 *  - "Plan en cours" derive de la LICENCE active (useLicense), pas du
 *    plan mock du tenant.
 *  - Style des cards plans aligne sur le site marketing (PricingPage)
 *  - Plus de badge "Recommande pour vous" - juste "Plan actuel".
 */
import {
  Body1,
  Caption1,
  Card,
  Title3,
  tokens,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { Key24Regular, WalletCreditCard24Regular } from '@fluentui/react-icons';
// v2026.06.129 - Wizard demande d offre commerciale
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SubscriptionRequestWizard, type SubscriptionPlan } from '../components/billing/SubscriptionRequestWizard';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import { useLicense } from '../auth/LicenseGate';
import { LicensePage } from './LicensePage';
import { plansApi, type PlanConfig, type PlanLang } from '../api/plansApi';

// =============================================================================
// 5 plans - features synchronisees avec la page tarif du site marketing
// (cf. app-marketing/src/i18n/locales/*.json pricing.plans). Source unique de
// verite : la matrice partagee dans le BACKLOG / pricing tableau v2026.06.
// =============================================================================
type PlanKey = 'trial' | 'starter' | 'pro' | 'business' | 'enterprise';

// Donnees resolues (langue courante) consommees par PlanCard.
interface PlanCardData {
  key:        PlanKey;
  monthlyEur: number | null;
  name:       string;
  target:     string;
  features:   string[];
  featured?:  boolean;
  free?:      boolean;
  partnerDiscountPct?: number | null;
}

// Definition de repli (cles i18n) si l'API /api/plans est indisponible.
interface PlanDefRaw {
  key:          PlanKey;
  monthlyEur:   number | null;       // null = sur devis
  targetKey:    string;              // i18n cle de la cible (taille tenant)
  featuresKeys: string[];            // liste d i18n cles
  featured?:    boolean;
  free?:        boolean;
  partnerDiscountPct?: number | null;
}

// i18n.language ('fr-BE'|'nl-BE'|'de-BE'|'en') -> code court des plans.
function planLang(lng: string): PlanLang {
  const short = (lng || 'fr').slice(0, 2);
  return (['fr', 'nl', 'de', 'en'].includes(short) ? short : 'fr') as PlanLang;
}

// Libellé de la réduction partenaire (par langue).
const DISCOUNT_LABEL: Record<PlanLang, string> = {
  fr: 'avec un partenaire intégrateur',
  nl: 'met een integratiepartner',
  de: 'mit einem Integrationspartner',
  en: 'with an integration partner',
};

// Config centralisee -> PlanCardData[] (langue courante). free -> 'trial'.
function fromConfig(plans: PlanConfig[], lang: PlanLang): PlanCardData[] {
  return [...plans].sort((a, b) => a.order - b.order).map((p) => {
    const tx = p.t?.[lang] ?? p.t?.fr;
    return {
      key:        (p.key === 'free' ? 'trial' : p.key) as PlanKey,
      monthlyEur: p.monthlyEur,
      name:       tx?.name ?? p.key,
      target:     tx?.target ?? '',
      features:   Array.isArray(tx?.features) ? tx!.features : [],
      featured:   p.featured,
      free:       p.free,
      partnerDiscountPct: p.partnerDiscountPct ?? null,
    };
  });
}

const FALLBACK_DEFS: PlanDefRaw[] = [
  {
    key:          'trial',
    monthlyEur:   0,
    targetKey:    'billing.target.trial',
    free:         true,
    featuresKeys: [
      'billing.features.trial.score',
      'billing.features.trial.viewByFamily',
      'billing.features.trial.scanFreqMonthly',
    ],
  },
  {
    key:          'starter',
    monthlyEur:   49,
    partnerDiscountPct: 5,
    targetKey:    'billing.target.starter',
    featuresKeys: [
      'billing.features.starter.scoreAndView',
      'billing.features.starter.mapping',
      'billing.features.starter.scanFreqWeekly',
      'billing.features.starter.reportPdf',
      'billing.features.starter.supportEmail',
    ],
  },
  {
    key:          'pro',
    monthlyEur:   149,
    partnerDiscountPct: 5,
    targetKey:    'billing.target.pro',
    featured:     true,
    featuresKeys: [
      'billing.features.pro.everythingStarter',
      'billing.features.pro.scanUnlimited',
      'billing.features.pro.reportsAll',
      'billing.features.pro.evidence',
      'billing.features.pro.multiUser',
      'billing.features.pro.supportPriority',
    ],
  },
  {
    key:          'business',
    monthlyEur:   499,
    partnerDiscountPct: 5,
    targetKey:    'billing.target.business',
    featuresKeys: [
      'billing.features.business.everythingPro',
      'billing.features.business.scheduling',
      'billing.features.business.alerts',
      'billing.features.business.sla4h',
      'billing.features.business.history',
      'billing.features.business.externalConnector',
      'billing.features.business.comingSoon',
      'billing.features.business.autoFix',
      'billing.features.business.autoDoc',
      'billing.features.business.incidentManager',
    ],
  },
  {
    key:          'enterprise',
    monthlyEur:   null,
    targetKey:    'billing.target.enterprise',
    featuresKeys: [
      'billing.features.enterprise.everythingBusiness',
      'billing.features.enterprise.multiTenant',
      'billing.features.enterprise.dpaCompliance',
      'billing.features.enterprise.onboardingDedicated',
    ],
  },
];

export function BillingPage() {
  const { t, i18n }  = useTranslation();
  const license      = useLicense();
  const hasLicense   = Boolean(license.hasLicense && license.status?.valid);
  const currentPlan  = (hasLicense ? (license.status?.plan ?? 'starter') : 'trial').toLowerCase();
  // v2026.06.129 - state Wizard demande d offre
  const [requestPlan, setRequestPlan] = useState<SubscriptionPlan | null>(null);

  // Grille tarifaire centralisee (editee en admin). Fallback i18n si API down.
  const lang = planLang(i18n.language);
  const plansQ = useQuery({ queryKey: ['plans'], queryFn: () => plansApi.list(), staleTime: 300_000, retry: false });
  const plans: PlanCardData[] = plansQ.data?.plans
    ? fromConfig(plansQ.data.plans, lang)
    : FALLBACK_DEFS.map((d) => ({
        key:        d.key,
        monthlyEur: d.monthlyEur,
        name:       t(`billing.plan.${d.key}`, { defaultValue: d.key }),
        target:     t(d.targetKey),
        features:   d.featuresKeys.map((k) => t(k)),
        featured:   d.featured,
        free:       d.free,
        partnerDiscountPct: d.partnerDiscountPct ?? null,
      }));

  const currentDef       = plans.find(p => p.key === currentPlan) ?? plans[0];
  const currentPlanLabel = currentDef?.name ?? currentPlan;

  return (
    <div>
      <PageHeader
        title={t('billing.mergedTitle')}
        description={t('billing.mergedSubtitle')}
      />

      {/* ====================================================================
          CADRE 1 — LICENCE (mount LicensePage en mode embedded)
          ==================================================================== */}
      <Card style={{ marginBottom: 24, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Key24Regular primaryFill={tokens.colorBrandForeground1} />
          <Title3 style={{ margin: 0 }}>{t('billing.section.licenseTitle')}</Title3>
        </div>
        <LicensePage embedded />
      </Card>

      {/* ====================================================================
          CADRE 2 — ABONNEMENT (comparatif des plans)
          ==================================================================== */}
      <Card style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <WalletCreditCard24Regular primaryFill={tokens.colorBrandForeground1} />
          <Title3 style={{ margin: 0 }}>{t('billing.section.subscriptionTitle')}</Title3>
        </div>

      {/* Bandeau "plan en cours" */}
      <MessageBar
        intent={hasLicense ? 'success' : 'info'}
        style={{ marginBottom: 24 }}
      >
        <MessageBarBody>
          <MessageBarTitle>
            {hasLicense
              ? t('billing.current.title', { plan: currentPlanLabel })
              : t('billing.current.titleTrial', { plan: currentPlanLabel })}
          </MessageBarTitle>
          {hasLicense
            ? (license.status?.validUntil
                ? t('billing.current.validUntil', { date: new Date(license.status.validUntil).toLocaleDateString(i18n.language) })
                : t('billing.current.validUntilUnknown'))
            : t('billing.current.activateHint')}
        </MessageBarBody>
      </MessageBar>

      {/* Comparatif plans - style marketing pricing-grid */}
      <div className="portal-pricing-grid">
        {plans.map(p => (
          <PlanCard key={p.key} plan={p} current={p.key === currentPlan}
            onRequest={(planKey) => setRequestPlan(planKey as SubscriptionPlan)} />
        ))}
      </div>

      {/* v2026.06.129 - Wizard demande d offre commerciale */}
      {requestPlan && (
        <SubscriptionRequestWizard plan={requestPlan} onClose={() => setRequestPlan(null)} />
      )}

      {/* Section "engagement annuel obligatoire" - explication contractuelle */}
      <Card style={{
        marginTop:    16,
        padding:      20,
        background:   'linear-gradient(135deg, #F0FBFC 0%, #FFFFFF 100%)',
        borderLeft:   '4px solid #2E8B96',
      }}>
        <Body1 block style={{
          fontWeight:   700,
          fontSize:     16,
          marginBottom: 8,
          color:        '#1F3D5C',
        }}>
          {t('billing.annual.title')}
        </Body1>
        <Body1 block style={{ marginBottom: 12, color: tokens.colorNeutralForeground2 }}>
          {t('billing.annual.body')}
        </Body1>
        <div style={{
          display:              'grid',
          gridTemplateColumns:  'repeat(auto-fit, minmax(220px, 1fr))',
          gap:                  12,
        }}>
          {(['commitment', 'lock'] as const).map((key) => (
            <div key={key} style={{
              padding:    12,
              background: tokens.colorNeutralBackground1,
              borderRadius: 8,
              border: `1px solid ${tokens.colorNeutralStroke2}`,
            }}>
              <Body1 style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 14 }}>
                {t(`billing.annual.benefits.${key}.title`)}
              </Body1>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                {t(`billing.annual.benefits.${key}.body`)}
              </Caption1>
            </div>
          ))}
        </div>
        <Caption1 style={{ display: 'block', marginTop: 12, color: tokens.colorNeutralForeground3 }}>
          {t('billing.annual.howTo')}
        </Caption1>
      </Card>

      <style>{`
        .portal-pricing-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 16px;
          margin-bottom: 32px;
        }
        @media (max-width: 1400px) { .portal-pricing-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 1000px) { .portal-pricing-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px)  { .portal-pricing-grid { grid-template-columns: 1fr; } }

        .portal-plan {
          display: flex;
          flex-direction: column;
          position: relative;
          padding: 28px 22px;
          background: ${tokens.colorNeutralBackground1};
          border: 1px solid ${tokens.colorNeutralStroke2};
          border-radius: 16px;
          transition: transform .2s ease, box-shadow .2s ease;
        }
        .portal-plan:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 24px rgba(15,38,56,.08);
        }
        .portal-plan.is-featured {
          border: 2px solid #2E8B96;
          box-shadow: 0 12px 36px rgba(15,38,56,.12);
          transform: scale(1.02);
        }
        .portal-plan.is-free {
          background: linear-gradient(180deg, #F0FBFC 0%, #FFFFFF 100%);
          border: 1px dashed #2E8B96;
        }
        .portal-plan.is-free .plan-name,
        .portal-plan.is-free .plan-price { color: #2E8B96; }
        .portal-plan.is-current {
          border: 2px solid #1F7A45;
          background: linear-gradient(180deg, #E6F4EC 0%, #FFFFFF 60%);
        }
        .plan-badge {
          position: absolute;
          top: -12px; left: 50%;
          transform: translateX(-50%);
          font-size: 11px; font-weight: 700;
          padding: 4px 12px;
          border-radius: 999px;
          text-transform: uppercase;
          letter-spacing: .05em;
          white-space: nowrap;
        }
        .plan-badge.featured { background: #2E8B96; color: #fff; }
        .plan-badge.free     { background: #1F7A85; color: #fff; }
        .plan-badge.current  { background: #1F7A45; color: #fff; }
        .plan-name {
          font-size: 1.15rem;
          font-weight: 700;
          color: ${tokens.colorBrandForeground1};
          margin-bottom: 4px;
        }
        .plan-target {
          font-size: .85rem;
          color: ${tokens.colorNeutralForeground3};
          margin-bottom: 18px;
        }
        .plan-price {
          font-size: 2.5rem;
          font-weight: 800;
          color: ${tokens.colorBrandForeground1};
          margin: 8px 0;
          line-height: 1;
        }
        .plan-price small {
          font-size: .9rem;
          font-weight: 500;
          color: ${tokens.colorNeutralForeground3};
          margin-left: 4px;
        }
        .plan-annual {
          font-size: .8rem;
          color: ${tokens.colorNeutralForeground3};
          margin-top: -4px;
          margin-bottom: 8px;
        }
        .plan-annual strong {
          color: ${tokens.colorNeutralForeground2};
          font-weight: 700;
        }
        .plan-description {
          font-size: .9rem;
          color: ${tokens.colorNeutralForeground2};
          margin-bottom: 12px;
          min-height: 56px;
        }
        .plan-features {
          list-style: none;
          padding: 0;
          margin: 8px 0 0;
          flex: 1;
        }
        .plan-features li {
          padding: 8px 0;
          color: ${tokens.colorNeutralForeground2};
          font-size: .92rem;
          border-top: 1px dashed ${tokens.colorNeutralStroke2};
        }
        .plan-features li:first-child {
          border-top: 0;
        }
        .plan-features li::before {
          content: '✓ ';
          color: #1F7A45;
          font-weight: 700;
        }
        .plan-cta {
          margin-top: 24px;
          padding: 12px 18px;
          width: 100%;
          border: 0;
          border-radius: 8px;
          font-size: .95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all .15s ease;
          font-family: inherit;
        }
        .plan-cta-primary {
          background: linear-gradient(135deg, #1F3D5C, #2E8B96);
          color: #fff;
        }
        .plan-cta-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(46,139,150,.4);
        }
        .plan-cta-ghost {
          background: transparent;
          color: ${tokens.colorBrandForeground1};
          border: 1.5px solid ${tokens.colorNeutralStroke2};
        }
        .plan-cta-ghost:hover {
          border-color: #2E8B96;
        }
        .plan-cta-disabled {
          background: ${tokens.colorNeutralBackground3};
          color: ${tokens.colorNeutralForeground3};
          cursor: not-allowed;
        }
      `}</style>

      {/* FAQ rapide */}
      <Body1 style={{ marginTop: 24, display: 'block', color: tokens.colorNeutralForeground2 }}>
        <strong>{t('billing.faqTitle')}</strong>
      </Body1>
      <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginTop: 4 }}>
        {t('billing.faqBody')}
      </Caption1>
      </Card>
      {/* Fin Cadre 2 Abonnement */}
    </div>
  );
}

// =============================================================================
// PlanCard - style marketing
// =============================================================================
function PlanCard({ plan, current, onRequest }: {
  plan:       PlanCardData;
  current:    boolean;
  /** v2026.06.129 - ouvre le SubscriptionRequestWizard quand l user clique CTA */
  onRequest?: (planKey: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const classes = [
    'portal-plan',
    plan.featured ? 'is-featured' : '',
    plan.free     ? 'is-free'     : '',
    current       ? 'is-current'  : '',
  ].filter(Boolean).join(' ');

  const planLabel = plan.name;

  // Prix mensuel = tarif INDICATIF. Le commitment reel est annuel (12 mois ferme).
  const monthlyIndicative = plan.monthlyEur;
  const yearlyTotal = plan.monthlyEur !== null && plan.monthlyEur > 0
    ? plan.monthlyEur * 12
    : null;

  return (
    <div className={classes}>
      {current && <span className="plan-badge current">{t('billing.badge.current')}</span>}
      {!current && plan.featured && <span className="plan-badge featured">{t('billing.badge.featured')}</span>}
      {!current && plan.free     && <span className="plan-badge free">{t('billing.badge.free')}</span>}

      <div className="plan-name">{planLabel}</div>
      <div className="plan-target">{plan.target}</div>

      <div className="plan-price">
        {monthlyIndicative === null ? (
          <span style={{ fontSize: '1.4rem' }}>{t('billing.priceOnDemand')}</span>
        ) : (
          <>
            {monthlyIndicative}<small>{t('billing.priceSuffix')}</small>
          </>
        )}
      </div>
      {/* Sous le mensuel : prix annuel total (engagement 12 mois) */}
      {yearlyTotal !== null && (
        <div className="plan-annual">
          {t('billing.yearlyCommitment', { amount: yearlyTotal.toLocaleString(i18n.language) })}
        </div>
      )}
      {/* Réduction partenaire (en rouge, sous l'engagement annuel) */}
      {plan.partnerDiscountPct ? (
        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#C13C3C', marginTop: 2 }}>
          −{plan.partnerDiscountPct}% {DISCOUNT_LABEL[planLang(i18n.language)]}
        </div>
      ) : null}

      <div className="plan-description">{t(`billing.description.${plan.key}`)}</div>

      <ul className="plan-features">
        {plan.features.map((f, i) => (
          <li key={i}>{f}</li>
        ))}
      </ul>

      {current ? (
        <button className="plan-cta plan-cta-disabled" disabled>
          {t('billing.cta.currentPlan')}
        </button>
      ) : plan.key === 'trial' ? (
        <button className="plan-cta plan-cta-ghost" disabled>
          {t('billing.cta.trialDefault')}
        </button>
      ) : plan.monthlyEur === null ? (
        <button className="plan-cta plan-cta-primary"
          onClick={() => onRequest?.(plan.key)}>
          {t('billing.cta.contactSales')}
        </button>
      ) : (
        <button className="plan-cta plan-cta-primary"
          onClick={() => onRequest?.(plan.key)}>
          {t('billing.cta.selectPlan')}
        </button>
      )}
    </div>
  );
}
